import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useState, useCallback } from "react";

import ConfirmDelete from "@/components/ConfirmDelete";

import SimpleHeader from "@/components/Headers/SimpleHeader";
import { getSites, deleteSite } from "@/services/siteServices/siteActions";
import { setAppMessage } from "@/services/appMessageServices/appMessageActions";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";

import DisplaySitesView from "./DisplaySitesView";

const DisplaySitesContainer = () => {
  const dispatch = useAppDispatch();

  const sitesFromStore = useAppSelector((s) => s.sites.sites || []);
  const permission = useCurrentPermission();

  const [alert, setAlert] = useState<React.ReactNode>(null);

  useEffect(() => {
    dispatch(getSites());
  }, [dispatch]);


  const questionAlert = useCallback((id: string) => {
    setAlert(
      // SCRUM-1021 follow-up lives in ConfirmDelete now: it awaits `onConfirm`
      // and only then calls `onSuccess`, so a failed delete cannot report
      // success. The rejection is surfaced centrally by appErrorMiddleware (the
      // shell hub reads state.errors).
      <ConfirmDelete
        title="Would you like to remove this Site?"
        onClose={hideAlert}
        onConfirm={() => dispatch(deleteSite(id)).unwrap()}
        onSuccess={() =>
          dispatch(setAppMessage("success", "Site Deleted with success"))
        }
      >
        Delete Operation
      </ConfirmDelete>
    );
  }, [dispatch]);

  const hideAlert = () => setAlert(null);

  // Errors are surfaced by the shell hub (GlobalNotifications) from state.errors.
  // getSites dispatches the raw axios error (no `.errors` array) so a local guard
  // never fired (SCRUM-989); deleteSite now routes its failures through
  // appErrorMiddleware into that same hub (SCRUM-1021 follow-up).

  const columns = [
    {
      header: "Logo",
      cell: ({ row }) => (
        <div>
          <img className="avatar rounded-circle mr-3" src={row.original.logo_pic || ""} alt="Logo" />
        </div>
      ),
    },
    { accessorKey: "country_code", header: "Country Code" },
    { accessorKey: "language_code", header: "Language" },
    { accessorKey: "domain", header: "Domain" },
    { accessorKey: "canonial_url", header: "Canonial Url" },
    { accessorKey: "_id", header: "Site ID" },
    { accessorFn: (row) => row.label?.[0]?.value?.label, header: "Label" },
    {
      header: "ACTION",
      cell: ({ row }) => (
        <div>
          <a
            href={`/admin/updating-site/${row.original._id}`}
            className="btn btn-primary btn-sm"
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-settings-gear-65" />
            </span>
            <span className="btn-inner--text">Details</span>
          </a>

          {(permission.edit || permission.publish) && (
            <button
              className="btn btn-danger btn-sm"
              onClick={() => questionAlert(row.original._id)}
            >
              <span className="btn-inner--icon mr-1">
                <i className="fas fa-trash" />
              </span>
              <span className="btn-inner--text">Delete</span>
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      {alert}
      <SimpleHeader name="Display Sites" parentName="Sites" />
      {/* SCRUM-2178: the store list itself, not a copy an effect fills one render late. */}
      <DisplaySitesView sites={sitesFromStore} columns={columns} />
    </>
  );
};

export default DisplaySitesContainer;
